import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton, Container, Collapse, Grid } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { keyframes } from '@mui/system';
import SEO from '../components/SEO';

// Keyframes for fade-in from bottom
const fadeInFromBottom = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const FAQ = ({ darkMode }) => {
  const [expandedIndex, setExpandedIndex] = useState(null);
  const questionRefs = useRef([]);

  const faqs = [
    {
      question: "What types of events do you cover?",
      answer: "We cover a wide range of events including weddings, birthday parties, corporate events, Christmas parties, Religious Festivals (Vesak, Poson, Kathina), and private parties. Our team specializes in creating custom lighting designs for each unique occasion."
    },
    {
      question: "How do I request a quote?",
      answer: "You can request a quote by contacting us through our website's contact form, emailing us directly at info@miraclelighting.lk, or calling our office. We typically respond within 24 hours and offer free consultations for all events."
    },
    {
      question: "Do you offer on-site consultations?",
      answer: "Yes, we offer complimentary on-site consultations to understand your event's requirements and provide personalized recommendations. This allows us to assess the venue, discuss your vision, and create the perfect lighting plan tailored to your specific needs."
    },
    {
      question: "What is your cancellation policy?",
      answer: "We understand plans can change. Cancellations made more than 30 days before the event receive a full refund. Between 15-30 days, we refund 50%. Less than 15 days notice is non-refundable. Please contact us as soon as possible if changes are needed."
    },
    {
      question: "Do you provide lighting for outdoor events?",
      answer: "Absolutely! We have weather-resistant equipment specifically designed for outdoor use. We can create beautiful ambient lighting for gardens, tents, patios, beach weddings, poolside events, and other outdoor spaces. Our team ensures safe installation with proper weatherproofing."
    },
    {
      question: "How far in advance should I book your services?",
      answer: "We recommend booking at least 2-4 weeks in advance for standard events, and 6-8 weeks for large weddings or special occasions like Christmas, Vesak, or Poson festivals. This allows us to conduct a venue visit, design the perfect lighting setup, and ensure all equipment is available for your event date."
    },
    {
      question: "What areas do you serve?",
      answer: "We serve all areas across Sri Lanka, including Colombo, Kandy, Galle, Negombo, and surrounding regions. Our team is willing to travel to any location to provide our professional lighting services for your special event."
    }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleQuestion = (index) => {
    if (expandedIndex === index) {
      setExpandedIndex(null);
    } else {
      setExpandedIndex(index);
      // Scroll to the question when expanded
      setTimeout(() => {
        questionRefs.current[index]?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }, 100);
    }
  };

  return (
    <>
      <SEO 
        title="FAQ | Miracle Lighting - Common Questions About Event Lighting in Sri Lanka"
        description="Find answers to frequently asked questions about our decorative lighting services for weddings, birthdays, Christmas, Vesak, Poson, Kathina festivals, pricing, installation, and booking process in Sri Lanka."
        keywords="lighting FAQ, event lighting questions, wedding lighting help, Miracle Lighting information, decorative lighting Sri Lanka, event lighting services, wedding lighting Colombo"
        url="https://miraclelighting.lk/faq"
      />
      
      <Container
        maxWidth={false}
        disableGutters
        sx={{
          minHeight: '100vh',
          backgroundImage: 'url("/images/About.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          px: 4,
          py: 6,
        }}
      >
        {/* Overlay for better readability - matching About Us page */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0)',
            zIndex: 0,
          }}
        />

        {/* Single-Column Layout - matching About Us page structure */}
        <Grid container spacing={6} sx={{ position: 'relative', zIndex: 1, flexDirection: 'column' }}>
          
          {/* FAQ Title Section */}
          <Grid item xs={12} sx={{ textAlign: 'center' }}>
            <Box sx={{ animation: `${fadeInFromBottom} 0.8s ease-out`, mt: 5 }}>
              <Typography
                               variant="h3"
                               gutterBottom
                               sx={{
                                 mt: 9,
                                 mb: 5,
                                 color: darkMode ? '#FFD700' : '#FFD700',
                                 textAlign: 'center',
                                 fontWeight: 'bold',
                                 
                                 position: 'relative',
                               
                               }}
                             >
                      Frequently Asked Questions
                    </Typography>
            </Box>
          </Grid>

          {/* FAQ Items Section */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2.5,
                width: '100%',
                maxWidth: '900px',
                margin: '0 auto',
                position: 'relative',
                zIndex: 1,
                animation: `${fadeInFromBottom} 0.8s ease-out 0.2s both`,
              }}
            >
              {faqs.map((faq, index) => (
                <Box
                  key={index}
                  ref={el => questionRefs.current[index] = el}
                  sx={{
                    borderRadius: '12px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    border: '1px solid rgba(255, 215, 0, 0.3)',
                    backgroundColor: darkMode ? '#f9e15b' : '#f9e15b',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      border: '1px solid rgba(255, 215, 0, 0.6)',
                      boxShadow: darkMode 
                        ? '0 6px 20px rgba(255, 215, 0, 0.15)' 
                        : '0 6px 20px rgba(0, 0, 0, 0.1)',
                    },
                  }}
                >
                  {/* Question Box */}
                  <Box
                    onClick={() => toggleQuestion(index)}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 3,
                      cursor: 'pointer',
                      backgroundColor: expandedIndex === index ? 'rgba(255, 215, 0, 0.1)' : 'transparent',
                      transition: 'background-color 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 215, 0, 0.1)',
                      },
                    }}
                  >
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        textAlign: 'left',
                        flexGrow: 1,
                        color: '#000000',
                        fontWeight: 600,
                        fontSize: { xs: '1rem', md: '1.1rem' },
                        pr: 2,
                      }}
                    >
                      {faq.question}
                    </Typography>
                    <IconButton
                      size="small"
                      sx={{ 
                        color: '#000000',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 215, 0, 0.2)',
                        },
                      }}
                    >
                      {expandedIndex === index ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </Box>
                  
                  {/* Answer Box with Collapse */}
                  <Collapse in={expandedIndex === index}>
                    <Box sx={{ 
                      p: 3,
                      pt: 0,
                      backgroundColor: 'transparent',
                      textAlign: 'left',
                      borderTop: '1px solid rgba(255, 215, 0, 0.2)'
                    }}>
                      <Typography variant="body1" sx={{ 
                        color: darkMode ? '#000000' : '#333333',
                        lineHeight: 1.8,
                        fontSize: { xs: '0.95rem', md: '1rem' },
                      }}>
                        {faq.answer}
                      </Typography>
                    </Box>
                  </Collapse>
                </Box>
              ))}
            </Box>
          </Grid>

        </Grid>
      </Container>
    </>
  );
};

export default FAQ;